"use client";
import { useEffect, useState, useSyncExternalStore } from "react";
import { getStorefrontPreferences, getServerStorefrontPreferences, subscribeStorefrontPreferences } from "./storefront-preferences-state";
import { storefrontGet, storefrontMutation, cleanShopPath } from "./storefront-api";
import { normalizeProducts, isRecord } from "./types";
import { useWebshopT } from "./i18n";
import { ProductGridBlockView } from "./storefront";
import { normalizeStorefrontFeatures, type StorefrontFeatures } from "../features";

const inFlight = new Map<string, Promise<unknown>>();
function loadWishlist(locale?: string, apiBasePath?: string) {
  const key = `${apiBasePath ?? ""}:${locale ?? ""}`;
  if (!inFlight.has(key)) inFlight.set(key, storefrontGet("wishlist", { locale }, apiBasePath).finally(() => inFlight.delete(key)));
  return inFlight.get(key)!;
}

export function useWishlist(locale?: string, apiBasePath?: string, active = true) {
  const [data, setData] = useState<Record<string, unknown>>({});
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!active) return;
    let mounted = true;
    let revision = 0;
    const update = (event: Event) => { revision++; setError(false); setData((event as CustomEvent<Record<string, unknown>>).detail); };
    const refresh = () => {
      const requested = revision;
      void loadWishlist(locale, apiBasePath).then((result) => {
        if (mounted && requested === revision && isRecord(result)) { setData(result); setError(false); }
      }).catch(() => { if (mounted) setError(true); });
    };
    refresh(); window.addEventListener("webshops:wishlist-updated", update);
    window.addEventListener("focus", refresh);
    return () => { mounted = false; window.removeEventListener("webshops:wishlist-updated", update); window.removeEventListener("focus", refresh); };
  }, [locale, apiBasePath, active]);
  return { data, error };
}

export function WishlistToggle({ productId, locale, editing = false, labels }: { productId: string; locale?: string; editing?: boolean; labels?: StorefrontFeatures["labels"] }) {
  const t = useWebshopT(locale, labels);
  const { data } = useWishlist(locale, undefined, !editing);
  const saved = Array.isArray(data.productIds) && data.productIds.includes(productId);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  return <div className="webshops-wishlist-control"><button type="button" className="webshops-wishlist-toggle" aria-pressed={saved} disabled={pending || editing}
    onClick={async () => {
      setPending(true); setError(false);
      try {
        const result = await storefrontMutation("wishlist", "POST", { productId, saved: !saved, locale });
        window.dispatchEvent(new CustomEvent("webshops:wishlist-updated", { detail: result }));
      } catch { setError(true); } finally { setPending(false); }
    }}><span aria-hidden="true">{saved ? "♥" : "♡"}</span><span>{saved ? t("module.webshops.wishlist.remove", "Remove from wishlist") : t("module.webshops.wishlist.add", "Save to wishlist")}</span></button>
    {error ? <span role="alert">{t("module.webshops.wishlist.error", "Wishlist could not be updated.")}</span> : null}</div>;
}

export function WishlistHeader({ locale }: { locale?: string }) {
  const features = useSyncExternalStore(subscribeStorefrontPreferences, getStorefrontPreferences, getServerStorefrontPreferences);
  const { data } = useWishlist(locale, undefined, features.wishlist);
  const t = useWebshopT(locale, normalizeStorefrontFeatures({ labels: data.labels }).labels);
  if (!features.wishlist || data.enabled !== true || typeof data.shopPath !== "string") return null;
  return <a className="webshops-header-wishlist" href={`${data.shopPath}/wishlist`}><span>{t("module.webshops.wishlist.title", "Wishlist")}</span> <span>{Number(data.count) || 0}</span></a>;
}

export function WishlistPage({ shopPath = "shop", locale, apiBasePath }: { shopPath?: string; locale?: string; apiBasePath?: string }) {
  const { data, error } = useWishlist(locale, apiBasePath);
  const preferences = normalizeStorefrontFeatures({ wishlist: true, labels: data.labels });
  const t = useWebshopT(locale, preferences.labels);
  return <div className="webshops-page webshops-wishlist-page">{error ? <p role="alert">{t("module.webshops.wishlist.error", "Wishlist could not be loaded.")}</p> :
    <ProductGridBlockView headingLevel={1} heading={t("module.webshops.wishlist.title", "Wishlist")} products={normalizeProducts(data.products)} locale={locale}
      features={preferences} productBasePath={`/${cleanShopPath(shopPath)}/product`} emptyMessage={t("module.webshops.wishlist.empty", "Your wishlist is empty.")} />}</div>;
}
